import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { throwError, EMPTY } from 'rxjs';
import { ToasterHelperService } from '../services/toasterHelper.service';
import { HttpStatusCode } from '../../app.enums';
import {
  isNullOrEmptyString,
  isNullOrUndefined,
} from '../../content/pages/components/general/event/shared/validator/undefined-or-null';
import { CommonService } from '../services/common.service';
import { IMessage } from '../../content/pages/pages.component';
import { AppUpdateService } from '../services/app-update.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private appUpdateService = inject(AppUpdateService);

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    const toasterService = this.injector.get(ToasterHelperService);
    const commonService = this.injector.get(CommonService);
    let clonedReq;
    if (typeof req !== 'undefined' && req !== null) {
      clonedReq = req.clone();
    }
    const smtxVersion: number = 20261013; // yyyymmdd
    return next.handle(clonedReq).pipe(
      tap(response => {
        if (
          response instanceof HttpResponse &&
          response.body &&
          !response.body.isSuccessful &&
          !isNullOrUndefined(response.body.errorMessage) &&
          !isNullOrEmptyString(response.body.errorMessage)
        ) {
          const loginMessageError = [
            '/manager/AuthenticationService/Authenticate',
            '/manager/AuthenticationService/ForgetPassword',
          ].some(r => response.url.includes(r));

          if (!loginMessageError) toasterService.error(response.body.errorMessage);
        } else if (
          response instanceof HttpResponse &&
          response.headers &&
          !isNullOrUndefined(response.headers.get('smtx-version')) &&
          !(smtxVersion >= parseInt(response.headers.get('smtx-version')!))
        ) {
          // MP-5600: Delegate version reload to AppUpdateService so unsaved edits
          // and event wizards are never interrupted with a forced logout.
          this.appUpdateService.handleBackendVersionMismatch();
        }
      }),
      catchError(errorResponse => {
        if (errorResponse instanceof HttpErrorResponse && errorResponse.error) {
          if (errorResponse.error.message) {
            this.handleError(errorResponse, toasterService, commonService);
          } else if (errorResponse.error.errors) {
            this.handleValidationError(errorResponse, toasterService);
          } else if (errorResponse.error.title) {
            toasterService.error(errorResponse.error.title);
          } else if (errorResponse.error['exception-handling-request']) {
            if (errorResponse?.status === HttpStatusCode.UNAUTHORIZED) return;
            else toasterService.error('An unknown error occurred');
          } else {
            // no need to action
          }
        } else if (
          errorResponse.status == HttpStatusCode.FORBIDDEN ||
          errorResponse.status == HttpStatusCode.UNAUTHORIZED
        ) {
          return EMPTY;
        } else {
          toasterService.error('An unknown error occurred');
        }
        return throwError(errorResponse);
      })
    );
  }

  private handleError(
    errorResponse: HttpErrorResponse,
    toasterService: ToasterHelperService,
    commonService: CommonService
  ) {
    const isErrorFromAddCreditCard = errorResponse.url.includes(
      '/manager/SettingService/AddCreditCardDetail'
    );
    const loginMessageError = [
      '/manager/AuthenticationService/Authenticate',
      '/manager/AuthenticationService/ForgetPassword',
    ].some(r => errorResponse.url.includes(r));

    try {
      if (!loginMessageError) {
        const htmlTagPattern = /<\/?[a-z][\s\S]*?>/i;
        const rawMessage = errorResponse?.error?.message;
        let message = Array.isArray(rawMessage) ? rawMessage[0] : (typeof rawMessage === 'string' ? rawMessage : undefined);
        if (!message && typeof errorResponse?.error === 'string') {
          message = errorResponse.error;
        }
        if (message && htmlTagPattern.test(message)) {
          const template: IMessage = {
            message: message,
            type: 'error',
          };
          commonService.setMessageTemplate(template);
        } else {
          if (errorResponse?.status === HttpStatusCode.UNAUTHORIZED) return;
          else toasterService.error(message || 'An unknown error occurred', isErrorFromAddCreditCard);
        }
      }
    } catch (err) {
      console.error('Error handling errorResponse:', err);
      if (errorResponse?.status === HttpStatusCode.UNAUTHORIZED) return;
      else {
        const rawMessage = errorResponse?.error?.message;
        let message = Array.isArray(rawMessage) ? rawMessage[0] : (typeof rawMessage === 'string' ? rawMessage : undefined);
        if (!message && typeof errorResponse?.error === 'string') {
          message = errorResponse.error;
        }
        toasterService.error(message || 'An unknown error occurred', isErrorFromAddCreditCard);
      }
    }
  }

  private handleValidationError(
    errorResponse: HttpErrorResponse,
    toasterService: ToasterHelperService
  ) {
    const loginMessageError = [
      '/manager/AuthenticationService/Authenticate',
      '/manager/AuthenticationService/ForgetPassword',
    ].some(r => errorResponse.url?.includes(r));

    if (loginMessageError) return;

    try {
      const errorMessages: string[] = [];
      const validationErrors = errorResponse.error.errors;

      if (Array.isArray(validationErrors)) {
        validationErrors.forEach(item => {
          if (typeof item === 'string') {
            errorMessages.push(item);
          } else if (item && typeof item === 'object' && item.message) {
            errorMessages.push(item.message);
          }
        });
      } else if (validationErrors && typeof validationErrors === 'object') {
        Object.values(validationErrors).forEach((val: any) => {
          if (Array.isArray(val)) {
            val.forEach(msg => {
              if (typeof msg === 'string') errorMessages.push(msg);
            });
          } else if (typeof val === 'string') {
            errorMessages.push(val);
          }
        });
      }

      const uniqueMessages = Array.from(new Set(errorMessages));
      if (uniqueMessages.length > 0) {
        uniqueMessages.forEach(msg => toasterService.error(msg));
      } else if (errorResponse.error.title) {
        toasterService.error(errorResponse.error.title);
      } else {
        toasterService.error('Validation error occurred');
      }
    } catch (err) {
      console.error('Error handling validation errors:', err);
      if (errorResponse?.status === HttpStatusCode.UNAUTHORIZED) return;
      else toasterService.error('Validation error occurred');
    }
  }
}
